if (!customElements.get('card-size-picker')) {
  customElements.define('card-size-picker', class extends HTMLElement {
    connectedCallback() {
      if (this.bound) {
        this.observer.observe(this.submit, { attributes: true, attributeFilter: ['aria-disabled'] });
        return;
      }
      this.bound = true;
      this.opener = this.querySelector('.card-size-picker__open');
      this.panel = this.querySelector('.card-size-picker__panel');
      this.backdrop = this.querySelector('.card-size-picker__backdrop');
      this.onOutside = (event) => { if (!this.contains(event.target) && !this.panel.contains(event.target)) this.close(false); };
      this.onKeys = (event) => {
        if (event.key === 'Escape' && !this.panel.hidden) { event.preventDefault(); this.close(); }
        if (event.key === 'Tab' && this.sheetHost && !this.panel.hidden) {
          const controls = [...this.panel.querySelectorAll('button:not(:disabled), a[href], input:not(:disabled), select:not(:disabled), textarea:not(:disabled)')]
            .filter(control => control.tabIndex >= 0 && !control.closest('[hidden]') && control.type !== 'hidden');
          const first = controls[0], last = controls[controls.length - 1];
          if (!this.panel.contains(document.activeElement)) { event.preventDefault(); (event.shiftKey ? last : first)?.focus(); return; }
          if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
          else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
        }
      };
      this.form = this.panel.querySelector('form');
      this.submit = this.form.querySelector('[type="submit"]');
      this.choices = [...this.panel.querySelectorAll('[data-variant-id]')];
      this.direct = this.choices.length === 1;
      if (this.direct) {
        this.opener.removeAttribute('aria-expanded');
        this.opener.removeAttribute('aria-controls');
        this.opener.setAttribute('aria-label', this.submit.textContent.trim());
        this.opener.disabled = this.choices[0].disabled;
      }
      this.opener.addEventListener('click', () => {
        if (!this.direct) { this.panel.hidden ? this.open() : this.close(); return; }
        if (this.choices[0].disabled || this.submit.getAttribute('aria-disabled') === 'true') return;
        this.form.elements.namedItem('id').value = this.choices[0].dataset.variantId;
        this.form.requestSubmit(this.submit);
      });
      this.querySelector('.card-size-picker__close').addEventListener('click', () => this.close());
      this.backdrop.addEventListener('click', () => this.close());
      this.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && !this.panel.hidden) { event.preventDefault(); this.close(); }
      });
      this.querySelectorAll('[data-variant-id]').forEach((button) => button.addEventListener('click', () => {
        const form = this.form;
        const submit = this.submit;
        if (button.disabled || submit.getAttribute('aria-disabled') === 'true') return;
        form.elements.namedItem('id').value = button.dataset.variantId;
        form.requestSubmit(submit);
      }));
      this.observer = new MutationObserver(() => {
        const busy = this.submit.getAttribute('aria-disabled') === 'true';
        this.panel.setAttribute('aria-busy', String(busy));
        if (this.direct) { this.opener.setAttribute('aria-busy', String(busy)); this.opener.disabled = busy || this.choices[0].dataset.originalDisabled === 'true'; }
        const errorRegion = this.panel.querySelector('.product-form__error-message-wrapper');
        const visibleError = errorRegion && !errorRegion.hidden && errorRegion.textContent.trim();
        if (this.wasBusy && !busy) {
          if (this.direct && (this.panel.querySelector('product-form').error || visibleError)) this.open();
          else if (!this.panel.querySelector('product-form').error && !visibleError) this.close(false);
        }
        this.wasBusy = busy;
        this.panel.querySelectorAll('[data-variant-id]').forEach(button => {
          if (button.dataset.originalDisabled == null) button.dataset.originalDisabled = String(button.disabled);
          button.disabled = busy || button.dataset.originalDisabled === 'true';
        });
      });
      this.observer.observe(this.submit, { attributes: true, attributeFilter: ['aria-disabled'] });
    }
    open() {
      document.querySelectorAll('card-size-picker').forEach(picker => { if (picker !== this) picker.close(false); });
      if (this.classList.contains('card-size-picker--sheet')) {
        this.sheetHost = document.createElement('div');
        this.sheetHost.className = 'card-size-picker-sheet-host';
        this.sheetHost.style.setProperty('--card-size-text', getComputedStyle(this).getPropertyValue('--card-size-text'));
        this.sheetHost.append(this.backdrop, this.panel);
        document.body.append(this.sheetHost);
        document.body.classList.add('card-variant-sheet-open');
        this.panel.setAttribute('role', 'dialog');
        this.panel.setAttribute('aria-modal', 'true');
      }
      this.panel.hidden = false;
      this.backdrop.hidden = !this.classList.contains('card-size-picker--sheet');
      this.opener.setAttribute('aria-expanded', 'true');
      document.addEventListener('click', this.onOutside);
      document.addEventListener('keydown', this.onKeys);
      (this.panel.querySelector('[data-variant-id]:not(:disabled)') || this.panel.querySelector('.card-size-picker__close')).focus();
    }
    close(restoreFocus = true) {
      if (!this.panel || this.panel.hidden) return;
      this.panel.hidden = true;
      this.backdrop.hidden = true;
      this.opener.setAttribute('aria-expanded', 'false');
      document.removeEventListener('click', this.onOutside);
      document.removeEventListener('keydown', this.onKeys);
      if (this.sheetHost) {
        this.append(this.backdrop, this.panel);
        this.sheetHost.remove();
        document.body.classList.remove('card-variant-sheet-open');
        this.sheetHost = null;
        this.panel.setAttribute('role', 'group');
        this.panel.removeAttribute('aria-modal');
      }
      if (restoreFocus) this.opener.focus();
    }
    disconnectedCallback() { this.close(false); this.observer?.disconnect(); document.removeEventListener('click', this.onOutside); document.removeEventListener('keydown', this.onKeys); }
  });
}

// Position against the drawn photo, including contain-mode letterboxing.
if (!customElements.get('card-image-quick')) {
  customElements.define('card-image-quick', class extends HTMLElement {
    connectedCallback() {
      this.image = this.closest('.card__inner')?.querySelector('.card__media img');
      if (!this.image) return;
      this.updateBounds = () => {
        const style = getComputedStyle(this.image);
        const width = this.image.clientWidth, height = this.image.clientHeight;
        if (this.closest('.related-products')?.dataset.quickAnchor === 'card' || style.objectFit !== 'contain' || !this.image.naturalWidth || !width || !height) {
          this.style.inset = '0px'; this.avoidBadgeOverlap(); return;
        }
        const scale = Math.min(width / this.image.naturalWidth, height / this.image.naturalHeight);
        const spareX = Math.max(0, width - this.image.naturalWidth * scale);
        const spareY = Math.max(0, height - this.image.naturalHeight * scale);
        const positions = style.objectPosition.split(' ');
        const percent = (value) => value?.endsWith('%') ? Math.max(0, Math.min(1, parseFloat(value) / 100)) : .5;
        const left = spareX * percent(positions[0]), top = spareY * percent(positions[1]);
        this.style.inset = `${top}px ${spareX-left}px ${spareY-top}px ${left}px`;
        this.avoidBadgeOverlap();
      };
      this.image.addEventListener('load', this.updateBounds);
      this.resize = new ResizeObserver(this.updateBounds);
      this.resize.observe(this.image);
      this.closest('.card__inner').querySelectorAll('.card__badge').forEach(badge => this.resize.observe(badge));
      this.updateBounds();
    }
    avoidBadgeOverlap() {
      this.style.setProperty('--card-quick-badge-shift', '0px');
      const button = this.querySelector('.card-size-picker__open, .quick-add__submit');
      if (!button) return;
      const box = button.getBoundingClientRect();
      const bounds = this.getBoundingClientRect();
      const topPosition = this.classList.contains('card-image-quick--top-left') || this.classList.contains('card-image-quick--top-right');
      let shift = 0;
      this.closest('.card__inner').querySelectorAll('.card__badge .badge').forEach(badge => {
        const rect = badge.getBoundingClientRect();
        if (!rect.width || !rect.height || box.right <= rect.left || box.left >= rect.right || box.bottom <= rect.top || box.top >= rect.bottom) return;
        shift = Math.max(shift, topPosition ? rect.bottom - box.top + 6 : box.bottom - rect.top + 6);
      });
      const room = topPosition ? bounds.bottom - box.bottom : box.top - bounds.top;
      this.style.setProperty('--card-quick-badge-shift', `${Math.max(0, Math.min(shift, room))}px`);
    }
    disconnectedCallback() { this.resize?.disconnect(); this.image?.removeEventListener('load', this.updateBounds); }
  });
}
